import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, CheckCircle2, XCircle, Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvitacionEntrenadorPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [info, setInfo] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [academiaIdForRedirect, setAcademiaIdForRedirect] = useState(null);

  useEffect(() => {
    loadInfo();
  }, [token]);

  const loadInfo = async () => {
    try {
      const res = await axios.get(`${API}/academia/invitacion/${token}`);
      setInfo(res.data);
      setAcademiaIdForRedirect(res.data.academia?.id);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Esta invitación ya fue utilizada.');
      } else {
        setError('Esta invitación no es válida o ha expirado.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/academia/invitacion/${token}/aceptar`, { password });
      if (res.data.academia_id) {
        setAcademiaIdForRedirect(res.data.academia_id);
      }
      setSuccess(true);
      toast.success('¡Cuenta creada exitosamente!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar la invitación');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Verificando invitación...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Invitación no válida</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-600 text-slate-300">
            Ir al inicio
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">¡Bienvenido al Equipo!</h2>
          <p className="text-slate-400 mb-6">
            Tu cuenta ha sido creada exitosamente. Ya puedes iniciar sesión con tu email y contraseña.
          </p>
          <Button onClick={() => navigate(`/academia/${academiaIdForRedirect}/login`)} className="bg-emerald-600 hover:bg-emerald-700">
            Iniciar Sesión
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Card className="p-8 bg-slate-800 border-slate-700">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Invitación de Entrenador
            </h1>
            <p className="text-slate-400 text-sm">
              Has sido invitado a unirte como entrenador
            </p>
          </div>

          {/* Info */}
          <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
            <p className="text-emerald-400 font-semibold text-lg">{info?.academia?.nombre}</p>
            <p className="text-slate-300 mt-2">
              <span className="text-slate-500">Entrenador:</span> {info?.entrenador?.nombre} {info?.entrenador?.apellido}
            </p>
            <p className="text-slate-400 text-sm">{info?.entrenador?.email}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Crear Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10 bg-slate-700 border-slate-600 text-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Confirmar Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetir contraseña"
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                'Aceptar Invitación'
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-6">
            Al aceptar, podrás gestionar clases y asistencia de la academia.
          </p>
        </Card>
      </div>
    </div>
  );
}
